import React, { useEffect, useState } from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { admissionDeadline } from "../data/mock";
import { useT } from "../i18n";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

function useCountdown(target) {
  const calc = () => {
    const diff = new Date(target).getTime() - Date.now();
    return { diff, d: Math.floor(diff / 864e5), h: Math.floor((diff / 36e5) % 24), m: Math.floor((diff / 6e4) % 60), s: Math.floor((diff / 1e3) % 60) };
  };
  const [t, setT] = useState(calc);
  useEffect(() => { const id = setInterval(() => setT(calc()), 1000); return () => clearInterval(id); }, [target]); // eslint-disable-line
  return t;
}

// Live countdown to the registration deadline. size: "compact" (hero) | "large" (admissions page)
export default function Countdown({ size = "compact" }) {
  const { t } = useT();
  const c = useCountdown(admissionDeadline.date);
  const large = size === "large";
  const closed = c.diff <= 0;
  const urgent = !closed && c.d < 7;
  const units = [[c.d, t("cd.days")], [c.h, t("cd.hours")], [c.m, t("cd.mins")], [c.s, t("cd.secs")]];

  if (closed) {
    return (
      <div data-testid="countdown-closed" style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: large ? "16px 22px" : "10px 16px", borderRadius: 14, background: "#FEF2F2", border: "1.5px solid rgba(220,38,38,0.3)", color: "#B91C1C", fontWeight: 700, fontSize: large ? 16 : 13 }}>
        <AlertTriangle size={large ? 20 : 16} /> {t("cd.closed")}
      </div>
    );
  }

  return (
    <div data-testid={`countdown-${size}`} className="countdown" style={{ display: "inline-flex", flexDirection: large ? "row" : "column", alignItems: large ? "center" : "flex-start", gap: large ? 26 : 8, padding: large ? "22px 28px" : "12px 16px", borderRadius: 18, background: large ? "linear-gradient(135deg,#7C2D12,#C2410C 60%,#EA580C)" : "rgba(255,255,255,0.85)", border: large ? "none" : "1.5px solid rgba(249,115,22,0.3)", boxShadow: large ? "0 24px 50px -22px rgba(124,45,18,0.7)" : "0 10px 24px -14px rgba(194,65,12,0.4)", backdropFilter: "blur(8px)", color: large ? "#fff" : "#1C1917", flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-flex", width: large ? 40 : 26, height: large ? 40 : 26, borderRadius: "50%", background: urgent ? "#DC2626" : "#FACC15", color: urgent ? "#fff" : "#713F12", alignItems: "center", justifyContent: "center", animation: urgent ? "pulseDot 1.4s infinite" : "none" }}><Clock size={large ? 20 : 14} /></span>
        <div>
          <div style={{ fontSize: large ? 12 : 10.5, letterSpacing: 1.2, fontWeight: 800, color: large ? "#FDE047" : "#EA580C" }}>{t("cd.label")}</div>
          <div style={{ fontSize: large ? 15 : 12, fontWeight: 600, opacity: large ? 0.95 : 0.8 }}>{admissionDeadline.label}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: large ? 12 : 6 }} data-testid="countdown-units">
        {units.map(([v, l], i) => (
          <div key={l} style={{ textAlign: "center" }}>
            <div className="kpi-num" data-testid={`cd-${l.toLowerCase()}`} style={{ minWidth: large ? 76 : 44, padding: large ? "10px 8px" : "6px 4px", borderRadius: 12, background: large ? "rgba(255,255,255,0.14)" : "linear-gradient(180deg,#FFF7ED,#FFEDD5)", border: large ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(249,115,22,0.25)", fontSize: large ? 34 : 20, lineHeight: 1.1, color: large ? "#fff" : (i === 0 && urgent ? "#DC2626" : "#C2410C"), fontVariantNumeric: "tabular-nums" }}>{pad(v)}</div>
            <div style={{ fontSize: large ? 11 : 9.5, fontWeight: 700, letterSpacing: 1, marginTop: 4, opacity: 0.85, textTransform: "uppercase" }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
